import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { GPSKalman, haversine } from "@/lib/gpsKalman";

// Rastreamento GPS em tempo real do entregador (v7 · fev/2026).
//
// MELHORIAS v7 sobre v5:
//   1. Filtro de distância: só envia se moveu ≥ MIN_DISTANCE_M metros.
//   2. Kalman: suaviza posições ruidosas (GPS urbano com prédios).
//   3. Fila offline: mantido (já estava em v5).
//   4. Heartbeat: POST /tracking/heartbeat a cada 60s independente de movimento.
//   5. Frequência adaptativa:
//      - Movimento (speed > MOVING_SPEED_MPS) → intervalo FAST (10s)
//      - Parado > IDLE_TIMEOUT_MS (2min) → intervalo SLOW (60s)
//      - Bateria < BATTERY_LOW_PCT (20%) → força SLOW + toast warning
//   6. Segurança: alertas de velocidade > 80 km/h são detectados no backend.
//
// Ativa-se sempre que houver pelo menos 1 entrega em "em_transito" (mesma
// regra que estava). Mantém compat com `enabledEmpresaIds`.
export function useLiveTracking(items, options = {}) {
  const {
    onError,
    enabledEmpresaIds,
    // Overrides opcionais (testes).
    // v7.1 (fev/2026 · fluidez): intervalos reduzidos pra rastreio mais suave.
    //   fast (>5 km/h)      = 3s
    //   veryFast (>40 km/h) = 2s (avenida/rodovia)
    //   slow (parado >30s ou bateria <20%) = 30s
    fastIntervalMs = 3000,       // era 10000
    veryFastIntervalMs = 2000,   // NOVO
    slowIntervalMs = 30000,      // era 60000
    heartbeatIntervalMs = 60000,
    minDistanceM = 10,
    movingSpeedMps = 5 / 3.6,      // 5 km/h → m/s (1.39)
    veryFastSpeedMps = 40 / 3.6,   // 40 km/h → m/s (11.11)
    idleTimeoutMs = 30 * 1000,     // era 2min, agora 30s
    batteryLowPct = 20,
  } = options;

  const TRACKING_STATUS = "em_transito";
  const QUEUE_KEY = "gps_ping_queue_v1";
  const MAX_QUEUE = 200;

  const active =
    Array.isArray(items) &&
    items.some((x) => {
      if (x.status !== TRACKING_STATUS) return false;
      if (enabledEmpresaIds && !enabledEmpresaIds.has(x.empresa_id)) return false;
      return true;
    });

  const watchIdRef = useRef(null);
  const timerRef = useRef(null);
  const heartbeatTimerRef = useRef(null);
  const rawPosRef = useRef(null);        // última leitura crua
  const smoothPosRef = useRef(null);     // após Kalman
  const lastSentPosRef = useRef(null);   // último ponto ENVIADO (pra filtro 10m)
  const lastMovementTsRef = useRef(Date.now());
  const sendingRef = useRef(false);
  const kalmanRef = useRef(new GPSKalman());
  const batteryRef = useRef({ level: 1, charging: false });
  const lowBatteryToastShownRef = useRef(false);

  const [queueSize, setQueueSize] = useState(() => readQueue().length);
  const [online, setOnline] = useState(
    typeof navigator === "undefined" ? true : navigator.onLine
  );
  const [currentMode, setCurrentMode] = useState("fast"); // fast/slow

  // ─── Battery Status API (fev/2026) ───────────────────────────────
  // API é opt-in: só Chrome mobile/Edge. Safari iOS não expõe. Se
  // indisponível, tratamos como "cheia" (sem economia).
  useEffect(() => {
    if (!("getBattery" in navigator)) return;
    let bat = null;
    let cancelled = false;
    navigator.getBattery().then((b) => {
      if (cancelled) return;
      bat = b;
      const sync = () => {
        batteryRef.current = { level: b.level, charging: b.charging };
        if (!b.charging && b.level * 100 < batteryLowPct && !lowBatteryToastShownRef.current) {
          lowBatteryToastShownRef.current = true;
          toast.warning(
            `Bateria em ${Math.round(b.level * 100)}% — GPS irá enviar menos vezes pra economizar carga.`,
            { duration: 8000 }
          );
        }
        if (b.charging || b.level * 100 >= batteryLowPct) {
          lowBatteryToastShownRef.current = false;
        }
      };
      sync();
      b.addEventListener?.("levelchange", sync);
      b.addEventListener?.("chargingchange", sync);
    }).catch(() => { /* API bloqueada — ignora */ });
    return () => {
      cancelled = true;
      // Battery Manager não tem removeEventListener padronizado em todos
      // browsers; deixamos a ref viva mas parar de reagir.
      bat = null;
    };
  }, [batteryLowPct]);

  // Online/offline
  useEffect(() => {
    const on = () => setOnline(true);
    const off = () => setOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("offline", off);
    };
  }, []);

  // ─── Loop principal: watchPosition + timers ──────────────────────
  useEffect(() => {
    if (!active) return;
    if (!("geolocation" in navigator)) {
      onError?.("Geolocalização não suportada neste dispositivo.");
      return;
    }
    kalmanRef.current.reset();
    lastSentPosRef.current = null;
    lastMovementTsRef.current = Date.now();

    const onSuccess = (pos) => {
      const raw = {
        latitude: pos.coords.latitude,
        longitude: pos.coords.longitude,
        accuracy: pos.coords.accuracy,
        speed: pos.coords.speed, // m/s (pode ser null)
        heading: pos.coords.heading,
        timestamp: pos.timestamp || Date.now(),
      };
      rawPosRef.current = raw;
      const smoothed = kalmanRef.current.update(raw);
      smoothPosRef.current = smoothed;

      // Detecta movimento pra reset do idle timer
      const speedMps = Number.isFinite(raw.speed) ? raw.speed : null;
      if (speedMps !== null && speedMps > movingSpeedMps) {
        lastMovementTsRef.current = Date.now();
      } else if (lastSentPosRef.current) {
        const d = haversine(
          lastSentPosRef.current.latitude, lastSentPosRef.current.longitude,
          smoothed.latitude, smoothed.longitude
        );
        if (d > minDistanceM) lastMovementTsRef.current = Date.now();
      }
    };
    const onErrCb = (err) => {
      console.warn("[live-tracking] geo error:", err?.message || err);
      onError?.(err?.message);
    };

    watchIdRef.current = navigator.geolocation.watchPosition(
      onSuccess, onErrCb,
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 25000 }
    );

    // Envio adaptativo — recalcula intervalo a cada tick.
    // v7.1: 3 níveis (very_fast/fast/slow) — mais responsivo em avenida.
    const scheduleNext = () => {
      const now = Date.now();
      const idleFor = now - lastMovementTsRef.current;
      const battLow = !batteryRef.current.charging && batteryRef.current.level * 100 < batteryLowPct;
      const speedMps = Number.isFinite(smoothPosRef.current?.speed) ? smoothPosRef.current.speed : 0;
      let mode = "fast";
      let nextMs = fastIntervalMs;
      if (battLow || idleFor > idleTimeoutMs) {
        mode = "slow"; nextMs = slowIntervalMs;
      } else if (speedMps > veryFastSpeedMps) {
        mode = "very_fast"; nextMs = veryFastIntervalMs;
      }
      setCurrentMode(mode);
      timerRef.current = setTimeout(async () => {
        await drainAndSend();
        scheduleNext();
      }, nextMs);
    };

    const drainAndSend = async () => {
      if (sendingRef.current) return;
      sendingRef.current = true;
      try {
        // 1) drena fila persistida primeiro (FIFO)
        if (navigator.onLine) {
          let queue = readQueue();
          while (queue.length > 0) {
            const head = queue[0];
            try {
              await api.post("/tracking/ping", head);
              queue = queue.slice(1);
              writeQueue(queue);
              setQueueSize(queue.length);
            } catch (e) {
              console.warn("[live-tracking] queue flush failed:", e?.message);
              break;
            }
          }
        }
        // 2) envia posição atual — só se moveu ≥ minDistanceM ou nunca enviou
        const p = smoothPosRef.current;
        if (!p) return;
        const last = lastSentPosRef.current;
        const distance = last
          ? haversine(last.latitude, last.longitude, p.latitude, p.longitude)
          : Infinity;
        if (last && distance < minDistanceM) {
          // Não moveu o bastante — pula esse envio (heartbeat separado avisa
          // que o app tá vivo mesmo parado).
          return;
        }
        const payload = {
          latitude: p.latitude,
          longitude: p.longitude,
          accuracy: p.accuracy,
          speed: p.speed,
          heading: p.heading,
          timestamp: new Date(p.timestamp || Date.now()).toISOString(),
        };
        if (!navigator.onLine) {
          enqueue(payload);
          setQueueSize(readQueue().length);
          return;
        }
        try {
          await api.post("/tracking/ping", payload);
          lastSentPosRef.current = { latitude: p.latitude, longitude: p.longitude };
        } catch (e) {
          console.warn("[live-tracking] ping failed, queuing:", e?.message);
          enqueue(payload);
          setQueueSize(readQueue().length);
        }
      } finally {
        sendingRef.current = false;
      }
    };

    // Heartbeat separado (60s) — confirma que o app tá vivo mesmo parado.
    // Não precisa esperar posição; se rede offline, silencia (backend não
    // pena por falta de heartbeat — só marca `heartbeat_stale`).
    const sendHeartbeat = async () => {
      if (!navigator.onLine) return;
      try { await api.post("/tracking/heartbeat"); }
      catch (e) { console.warn("[live-tracking] heartbeat failed:", e?.message); }
    };

    const firstSendTimer = setTimeout(async () => {
      await drainAndSend();
      scheduleNext();
    }, 3000);
    heartbeatTimerRef.current = setInterval(sendHeartbeat, heartbeatIntervalMs);
    // dispara heartbeat imediato pra atualizar last_gps_heartbeat_at
    sendHeartbeat();

    const onOnline = () => { drainAndSend(); };
    window.addEventListener("online", onOnline);

    return () => {
      clearTimeout(firstSendTimer);
      window.removeEventListener("online", onOnline);
      if (watchIdRef.current !== null) {
        try { navigator.geolocation.clearWatch(watchIdRef.current); }
        catch (e) { console.warn("clearWatch failed", e); }
        watchIdRef.current = null;
      }
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
      if (heartbeatTimerRef.current) {
        clearInterval(heartbeatTimerRef.current);
        heartbeatTimerRef.current = null;
      }
      rawPosRef.current = null;
      smoothPosRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function readQueue() {
    try {
      const raw = localStorage.getItem(QUEUE_KEY);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch { return []; }
  }
  // Nota de segurança: a fila offline usa localStorage porque é a ÚNICA API web
  // que persiste entre reload/soft-kill do PWA (sessionStorage some, IndexedDB
  // é overkill p/ <500 items). Os dados armazenados são coordenadas GPS
  // efêmeras (lat/long/timestamp) — não contêm tokens, senhas ou PII sensível
  // sob LGPD; expiram naturalmente no próximo /tracking/ping bem-sucedido.
  // O entregador já concede consentimento explícito de rastreamento no login.
  function writeQueue(arr) {
    try { localStorage.setItem(QUEUE_KEY, JSON.stringify(arr)); }
    catch (err) { console.warn("localStorage cheio", err); }
  }
  function enqueue(item) {
    const q = readQueue();
    q.push(item);
    while (q.length > MAX_QUEUE) q.shift();
    writeQueue(q);
  }

  return { tracking: active, queueSize, online, mode: currentMode };
}
